/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { keyframes as emotionKeyframes } from '@emotion/react';
import { theme, keyframes } from '../../styles/theme';

/* ============================================
   KEYFRAMES
   ============================================ */
const spinAnimation = emotionKeyframes`${keyframes.spin}`;

/* ============================================
   AUTH CONTAINER
   ============================================ */
export const AuthContainerWrapper = styled.div`
  width: 100%;
  max-width: 400px;
  border-radius: ${theme.radius.xl};
  overflow: hidden;
  box-shadow: ${theme.shadow.lg};
`;

export const AuthCard = styled.div`
  background-color: ${theme.colors.bgWhite};
  padding: ${theme.spacing[6]};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: ${theme.spacing[6]};
`;

/* ============================================
   AUTH HEADER
   ============================================ */
export const AuthHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
`;

export const AuthHeaderTitle = styled.h1`
  font-size: ${theme.fontSize['2xl']};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text};
  line-height: ${theme.lineHeight.loose};
  margin: 0;
  font-family: ${theme.fontFamily.base};
`;

export const AuthHeaderSubtitle = styled.p`
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.normal};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.lineHeight.relaxed};
  margin: 0;
  font-family: ${theme.fontFamily.base};
`;

/* ============================================
   FORM FIELD
   ============================================ */
export const FormFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: ${theme.spacing[1]};
`;

export const FormFieldLabel = styled.label`
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.normal};
  color: ${theme.colors.text};
  line-height: ${theme.lineHeight.relaxed};
  font-family: ${theme.fontFamily.base};
`;

export const FormFieldInput = styled.input`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  align-self: stretch;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.radius.sm};
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.bgWhite};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.normal};
  line-height: ${theme.lineHeight.normal};
  color: ${theme.colors.text};
  font-family: ${theme.fontFamily.base};
  transition: border-color ${theme.transition.base};

  &::placeholder {
    color: ${theme.colors.textMuted};
    font-size: ${theme.fontSize.sm};
    font-weight: ${theme.fontWeight.normal};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.borderFocus};
  }
`;

/* ============================================
   PASSWORD FIELD
   ============================================ */
export const PasswordFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: ${theme.spacing[1]};
`;

export const PasswordFieldLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const PasswordFieldLabel = styled.label`
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.normal};
  color: ${theme.colors.text};
  line-height: ${theme.lineHeight.relaxed};
  font-family: ${theme.fontFamily.base};
`;

export const PasswordFieldForgotLink = styled.a`
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.normal};
  color: ${theme.colors.textSecondary};
  text-decoration: none;
  transition: color ${theme.transition.fast};
  font-family: ${theme.fontFamily.base};
  cursor: pointer;
`;

export const PasswordFieldInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const PasswordFieldInput = styled.input`
  width: 100%;
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  padding-right: 2.5rem;
  border-radius: ${theme.radius.sm};
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.bgWhite};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.normal};
  line-height: ${theme.lineHeight.normal};
  color: ${theme.colors.text};
  font-family: ${theme.fontFamily.base};
  transition: border-color ${theme.transition.base};

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.borderFocus};
  }
`;

export const PasswordFieldToggleButton = styled.button`
  position: absolute;
  right: ${theme.spacing[1]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textSecondary};
  transition: color ${theme.transition.fast};
`;

/* ============================================
   SUBMIT BUTTON
   ============================================ */
export const SubmitButton = styled.button`
  border-radius: ${theme.radius.sm};
  background-color: ${theme.colors.primary};
  height: ${theme.sizes.button.height};
  width: 100%;
  display: flex;
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  align-self: stretch;
  color: ${theme.colors.bgWhite};
  font-weight: ${theme.fontWeight.semibold};
  font-family: ${theme.fontFamily.manrope};
  font-size: ${theme.fontSize.base};
  line-height: normal;
  border: none;
  cursor: pointer;
  transition: background-color ${theme.transition.base};

  &:hover:not(:disabled) {
    background-color: ${theme.colors.primaryHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SubmitButtonIcon = styled.div`
  width: ${theme.sizes.icon};
  height: ${theme.sizes.icon};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const SubmitButtonSpinner = styled.div`
  width: ${theme.sizes.icon};
  height: ${theme.sizes.icon};
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${spinAnimation} 1s linear infinite;

  svg {
    width: 100%;
    height: 100%;
  }
`;

/* ============================================
   OAUTH BUTTON
   ============================================ */
export const OAuthButton = styled.button<{ $variant?: 'full' | 'short' | 'iconOnly' }>`
  display: flex;
  width: 100%;
  height: ${theme.sizes.button.heightOAuth};
  padding: ${(props: { $variant?: 'full' | 'short' | 'iconOnly' }) =>
    props.$variant === 'short' || props.$variant === 'iconOnly'
      ? theme.spacing[2]
      : `${theme.spacing[2]} ${theme.spacing[3]}`};
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${(props: { $variant?: 'full' | 'short' | 'iconOnly' }) =>
    props.$variant === 'iconOnly'
      ? '0'
      : props.$variant === 'short'
        ? theme.spacing[2]
        : theme.spacing[3]};
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.borderGray};
  background-color: ${theme.colors.bgWhite};
  box-shadow: ${theme.shadow.md};
  color: ${theme.colors.zinc900};
  text-align: center;
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.medium};
  line-height: ${theme.lineHeight.normal};
  cursor: pointer;
  transition: all ${theme.transition.base};
  font-family: ${theme.fontFamily.base};

  &:hover:not(:disabled) {
    background-color: ${theme.colors.bgHover};
    border-color: ${theme.colors.borderHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const OAuthButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${theme.sizes.iconSm};
  height: ${theme.sizes.iconSm};

  svg {
    width: 100%;
    height: 100%;
  }
`;

/* ============================================
   OAUTH PROVIDERS
   ============================================ */
export const OAuthProvidersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  width: 100%;
`;

/* ============================================
   AUTH LINK
   ============================================ */
export const AuthLinkWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textSecondary};
  font-family: ${theme.fontFamily.base};
`;

export const AuthLinkText = styled.span`
  font-weight: ${theme.fontWeight.normal};
`;

export const AuthLinkLink = styled.a`
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text};
  text-decoration: none;
  transition: opacity ${theme.transition.fast};
  cursor: pointer;
`;

/* ============================================
   AUTH DIVIDER
   ============================================ */
export const AuthDividerWrapper = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  width: 100%;
  gap: ${theme.spacing[3]};
`;

export const AuthDividerLine = styled.div`
  flex: 1;
  border-top: 1px solid ${theme.colors.border};
`;

export const AuthDividerText = styled.span`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.fontWeight.normal};
  font-family: ${theme.fontFamily.manrope};
`;

/* ============================================
   ERROR BANNER
   ============================================ */
export const ErrorBannerWrapper = styled.div`
  padding: ${theme.spacing[3]};
  background-color: ${theme.colors.errorBg};
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.error};
`;

export const ErrorBannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

export const ErrorBannerIcon = styled.div`
  width: ${theme.sizes.iconLg};
  height: ${theme.sizes.iconLg};
  flex-shrink: 0;
  color: ${theme.colors.error};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const ErrorBannerText = styled.p`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.error};
  font-weight: ${theme.fontWeight.normal};
  font-family: ${theme.fontFamily.base};
  margin: 0;
`;

/* ============================================
   AUTH BRANDING
   ============================================ */
export const AuthBrandingWrapper = styled.div`
  background-color: ${theme.colors.bgLight};
  padding: ${theme.spacing[4]} ${theme.spacing[2]};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

export const AuthBrandingText = styled.p`
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.text};
  font-family: ${theme.fontFamily.manrope};
  margin: 0;
`;

/* ============================================
   VERIFICATION CODE INPUT
   ============================================ */
export const VerificationCodeInputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: center;
`;

export const VerificationCodeInput = styled.input`
  width: ${theme.sizes.verifyCode};
  height: ${theme.sizes.verifyCode};
  text-align: center;
  font-size: ${theme.fontSize.base};
  font-weight: ${theme.fontWeight.semibold};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  background-color: ${theme.colors.bgWhite};
  color: ${theme.colors.text};
  transition: border-color ${theme.transition.base};
  font-family: ${theme.fontFamily.manrope};

  &:focus {
    outline: none;
    border-color: ${theme.colors.borderFocus};
  }
`;

/* ============================================
   PASSWORD STRENGTH INDICATOR
   ============================================ */
export const PasswordStrengthWrapper = styled.div`
  margin-top: ${theme.spacing[2]};
`;

export const PasswordStrengthFill = styled.div`
  height: 100%;
  transition:
    width ${theme.transition.base},
    background-color ${theme.transition.base};
`;

export const PasswordStrengthText = styled.span`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textSecondary};
  font-family: ${theme.fontFamily.base};
`;

export const PasswordStrengthRequirements = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textGray};
  font-family: ${theme.fontFamily.base};
`;

export const PasswordStrengthRequirement = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;
